import React from 'react'
import styled from 'styled-components'
import {
  formatEventExtendedDate,
  formatEventTimeZoneLabel
} from 'components_v2/Events/formatEvent'

export const Page = styled.div`
  background: #fff;
`

export const Hero = styled.div`
  position: relative;
  height: 600px;
  overflow: hidden;
  background-color: #ccc;
  @media (max-width: 768px) {
    height: 450px;
  }
`

// Blurred, scaled copy of the cover shown only when Luma returns an image
// smaller than the banner. It fills the letterbox areas behind the contained
// foreground image so an undersized asset never looks stretched/pixelated.
export const HeroBackdrop = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${(props) => props.imageUrl});
  background-size: cover;
  background-position: center;
  filter: blur(24px);
  transform: scale(1.1);
`

// Foreground cover image. Falls back to `contain` for low-res assets so we
// display them at native size (sharp) rather than upscaling to fill.
export const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: ${(props) => (props.$contain ? 'contain' : 'cover')};
  object-position: center;
`

export const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.25) 0%,
    rgba(0, 0, 0, 0) 30%,
    rgba(0, 0, 0, 0) 55%,
    rgba(0, 0, 0, 0.65) 100%
  );
`

export const HeroContent = styled.div`
  position: absolute;
  left: 80px;
  bottom: 70px;
  color: white;
  z-index: 2;

  @media (max-width: 768px) {
    left: 25px;
    right: 25px;
    bottom: 35px;
  }
`

export const DateWrapper = styled.div`
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0.308px;
  color: black;
  display: inline-block;
  margin-bottom: 43px;
  padding: 4px 12px;
  align-items: center;
  border-radius: 18px;
  border: 1px #ccc;
  background: rgba(255, 255, 255, 0.85);

  @media (max-width: 768px) {
    position: absolute;
    top: -350px;
  }
`

export const Title = styled.h1`
  color: #fff;
  font-family: 'Proxima-Nova';
  font-size: 32px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  text-transform: uppercase;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`

export const ContentWrapper = styled.div`
  max-width: 90%;
  margin: auto;
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 60px;
  padding: 48px 60px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 32px 16px;
    max-width: 100%;
  }
`

export const MoreEventWrapper = styled.div`
  max-width: 90%;
  margin: auto auto -98px auto;
  @media (max-width: 768px) {
    padding: 32px 16px;
    max-width: 100%;
  }
`

export const Card = styled.div`
  top: 30px;
  background: white;
`

export const MoreEventCards = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (min-width: 768px) and (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

export const CardFirst = styled.div`
  border: 0.667px solid #a3a3a3;
  padding: 24px;
`

export const CardSecond = styled.div`
  border: 0.667px solid #a3a3a3;
  padding: 24px;
`

export const Badge = styled.div`
  color: #3c4b37;
  font-family: 'Proxima-Nova';
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 115%;
  text-transform: uppercase;
  padding: 3px 8px;
  border: 0.667px solid #758e6d;
  background: #d8e1d5;
  display: inline-block;
  margin-bottom: 16px;
`

export const BadgeSold = styled.div`
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 115%;
  text-transform: uppercase;
  padding: 3px 8px;
  border: 0.667px solid #636363;
  background: #e1e1e1;
  display: inline-block;
  margin-bottom: 16px;
`

export const BadgeFillingFast = styled.div`
  color: #826646;
  font-family: 'Proxima-Nova';
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 115%;
  text-transform: uppercase;
  padding: 3px 8px;
  border: 0.667px solid #826646;
  background: #f1e8dd;
  display: inline-block;
  margin-bottom: 16px;
`

export const EventName = styled.h2`
  color: #000;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.352px;
  margin-bottom: 16px;
  text-transform: uppercase;
`

export const ExtendedDateComponent = ({
  startAt,
  endAt,
  timezone,
  br = true
}) => {
  const start = formatEventExtendedDate(startAt, timezone)
  const end = formatEventExtendedDate(endAt, timezone)
  const zoneLabel = formatEventTimeZoneLabel(startAt, timezone)
  if (!start || !end) return null

  return (
    <p>
      <span>{`${start} - `}</span>
      {br && <br />}
      <span>{zoneLabel ? `${end} ${zoneLabel}` : end}</span>
    </p>
  )
}

export const Info = styled.div`
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.308px;
  margin-bottom: 10px;
  display: flex;
  column-gap: 9px;
`

export const Progress = styled.div`
  margin-bottom: 20px;
  background: #ebe7e0;
  height: 2px;
  width: 80%;
  margin-top: 6px;
`

export const ProgressBar = styled.div`
  width: ${(props) => props.percentage}%;
  background: #8ea57a;
  height: 100%;
`

export const RSVPButton = styled.button`
  color: #fff;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  display: flex;
  height: 34px;
  padding: 12px 24px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  width: 100%;
  border-radius: 360px;
  background: #000;
  border: 0;
`

export const BottomLinks = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 14px;

  a {
    color: #000;
    text-align: center;
    font-family: 'Proxima-Nova';
    font-size: 14px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0.308px;
    text-decoration: underline;
  }
`

export const HRLine = styled.div`
  border-top: 0.667px solid #e0e0e0;
  margin: 16px 0;
`

export const AddToCalender = styled.div`
  display: flex;
  column-gap: 9px;
`

export const SubInfo = styled.div`
  color: #636363;
  font-family: 'Proxima-Nova';
  font-size: 12px;
  font-weight: 400;
  line-height: 130%;
  letter-spacing: 0.48px;
`

export const ButtonWrapper = styled.div`
  @media (max-width: 768px) {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: #fff;
    padding: 10px 25px 40px 10px;
    z-index: 100;
  }

  .rsvp {
    background: #000;
    color: #fff;
    text-align: center;
    font-family: 'Proxima-Nova';
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    padding: 8px 24px;
    width: 100%;
  }

  div {
    .eventCancelGroup {
      background: #000;
      color: #fff;
      text-align: center;
      font-family: 'Proxima-Nova';
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 24px;
      letter-spacing: 0.8px;
      text-transform: uppercase;
      padding: 8px 24px;
      width: 100%;

      &.cancel {
        background: #fff;
        color: #000;
        border: 1px solid #a3a3a3;
      }
    }
  }
`

export const SpotCount = styled.div`
  color: #7a7267;
  font-family: 'Proxima-Nova';
  font-size: 10px;
  font-weight: 600;
  line-height: 15px;
  letter-spacing: 0.25px;
  text-transform: uppercase;
`

export const Location = styled.div`
  color: #7a7267;
  font-family: 'Proxima-Nova';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0.308px;
  margin-top: 8px;
`

export const MainContent = styled.div``

export const ProgressBarWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

export const CalendarIcon = ({ color = 'black', ...props }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='18'
    height='19'
    viewBox='0 0 18 19'
    fill='none'
    {...props}
  >
    <g clipPath='url(#clip0_118_8411)'>
      <path
        d='M5.5 2H12.5V0H13.5V2H18V19H0V2H4.5V0H5.5V2ZM1 7.5V18H17V7.5H1ZM1 6.5H17V3H13.5V5H12.5V3H5.5V5H4.5V3H1V6.5Z'
        fill={color}
      />
    </g>
    <defs>
      <clipPath id='clip0_118_8411'>
        <rect width='18' height='19' fill='white' />
      </clipPath>
    </defs>
  </svg>
)

export const Section = styled.section`
  margin-bottom: 40px;
  border-bottom: 1px solid #a3a3a3;
  padding-bottom: 40px;
  /* padding-bottom: 50px; */
`

export const SectionTitle = styled.h2`
  color: #000;
  font-family: 'Proxima-Nova';
  font-size: 18px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  margin-bottom: 20px;
`

export const Description = styled.div`
  white-space: pre-line;
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.352px;

  &.markdown {
    font-size: 1rem;
    line-height: 1.7;
    color: #1f2937;

    h1 {
      font-size: 2rem;
      font-weight: 700;
      line-height: 1.2;
      margin: 1.5rem 0 1rem;
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 700;
      line-height: 1.3;
      margin: 1.5rem 0 0.75rem;
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      line-height: 1.4;
      margin: 1.25rem 0 0.75rem;
    }

    h4 {
      font-size: 1.125rem;
      font-weight: 600;
      line-height: 1.4;
      margin: 1rem 0 0.5rem;
    }

    h5,
    h6 {
      font-size: 1rem;
      font-weight: 600;
      line-height: 1.5;
      margin: 1rem 0 0.5rem;
    }

    p {
      margin: 0 0 1rem;
    }

    ul,
    ol {
      margin: 0 0 1rem 1.5rem;
      padding: 0;
    }

    li {
      margin: 0.25rem 0;
    }

    a {
      text-decoration: underline;

      &:hover {
        text-decoration: none;
      }
    }

    code {
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: 0.9em;
      background: #f3f4f6;
      padding: 0.15em 0.35em;
      border-radius: 4px;
    }

    pre {
      background: #111827;
      color: #f9fafb;
      padding: 1rem;
      border-radius: 8px;
      overflow-x: auto;

      code {
        background: none;
        padding: 0;
        color: inherit;
        font-size: inherit;
      }
    }

    blockquote {
      margin: 1rem 0;
      padding-left: 1rem;
      border-left: 4px solid #d1d5db;
      color: #4b5563;
    }

    img {
      display: block;
      max-width: max(320px, 50%);
      height: auto;
      border-radius: 4px;
      margin: 1rem 0;
    }

    table {
      width: 100%;
      border-collapse: collapse;
      margin: 1rem 0;
    }

    th,
    td {
      border: 1px solid #e5e7eb;
      padding: 0.75rem;
      text-align: left;
    }

    th {
      background: #f9fafb;
      font-weight: 600;
    }

    hr {
      border: 0;
      border-top: 1px solid #e5e7eb;
      margin: 2rem 0;
    }
  }
`

export const BulletList = styled.ul`
  color: #555;
  line-height: 2;
  padding-left: 22px;
  font-size: 18px;
`

export const MapWrapper = styled.div`
  width: 100%;
  height: 338.667px;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }

  @media (max-width: 768px) {
    height: 320px;
  }
`

export const MeetingLink = styled.div`
  background: lightgray;
  padding: 24px;
  font-size: 18px;
  font-family: 'Proxima-Nova';
`

export const LocationIcon = ({ color = '#141414', size = 20, ...props }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={size}
    height={size}
    viewBox='0 0 20 20'
    fill='none'
    {...props}
  >
    <path
      d='M9.99766 1.24973C6.54789 1.24973 3.74902 3.8318 3.74902 7.01198C3.74902 12.5 9.99766 18.7459 9.99766 18.7459C9.99766 18.7459 16.2463 12.5 16.2463 7.01198C16.2463 3.8318 13.4474 1.24973 9.99766 1.24973Z'
      stroke={color}
      strokeWidth={1.00189}
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M10.0003 10.8333C11.8413 10.8333 13.3337 9.34095 13.3337 7.5C13.3337 5.65906 11.8413 4.16667 10.0003 4.16667C8.15938 4.16667 6.66699 5.65906 6.66699 7.5C6.66699 9.34095 8.15938 10.8333 10.0003 10.8333Z'
      stroke={color}
      strokeWidth={1.00189}
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
)

export const VenueTitle = styled.div`
  color: #000;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.352px;
`

export const VenueSubInfo = styled.div`
  color: #3b3b3b;
  font-family: 'Proxima-Nova';
  font-size: 14px;
  font-weight: 400;
  line-height: 21px;
  letter-spacing: 0.308px;
  margin-bottom: 16px;
`
